import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { toast } from "sonner";
import {
  Sparkles,
  Zap,
  ArrowLeft,
  Check,
  Loader2,
  CreditCard,
  TrendingUp,
  Image,
  Video,
  Mic,
  FileText
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CREDIT_PACKAGES = [
  {
    id: "credits_500",
    credits: 500,
    price: 49,
    perCredit: 0.098,
    popular: false,
    description: "Great for getting started",
    uses: [
      "~50 AI images",
      "~25 video clips",
      "~100 social posts"
    ]
  },
  {
    id: "credits_2000",
    credits: 2000,
    price: 149,
    perCredit: 0.0745,
    popular: true,
    description: "Best value for regular users",
    uses: [
      "~200 AI images",
      "~100 video clips",
      "~400 social posts"
    ]
  },
  {
    id: "credits_5000",
    credits: 5000,
    price: 299,
    perCredit: 0.0598,
    popular: false,
    description: "Power user package",
    uses: [
      "~500 AI images",
      "~250 video clips",
      "~1000 social posts"
    ]
  }
];

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const AICreditsPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(null);
  const [currentCredits, setCurrentCredits] = useState(0);
  const [loadingCredits, setLoadingCredits] = useState(true);

  useEffect(() => {
    if (user && token) {
      fetchCredits();
    }
  }, [user, token]);

  const fetchCredits = async () => {
    try {
      const res = await fetch(`${API}/api/merchant/ai-credits`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentCredits(data.balance || 0);
      }
    } catch (error) {
      console.error("Failed to fetch credits:", error);
    } finally {
      setLoadingCredits(false);
    }
  };

  const handlePurchase = async (pkg) => {
    if (!user) {
      toast.error("Please log in to purchase credits");
      return;
    }

    setLoading(pkg.id);
    try {
      const res = await fetch(`${API}/api/stripe/ai-credits/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({
          package_id: pkg.id,
          credits: pkg.credits,
          price: pkg.price,
          success_url: `${window.location.origin}/merchant/ai-credits?success=true&session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/merchant/ai-credits?canceled=true`
        })
      });

      const data = await res.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || "Failed to create checkout session");
      }
    } catch (error) {
      toast.error(error.message || "Failed to process. Please try again.");
      setLoading(null);
    }
  };

  // Check for success redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const sessionId = urlParams.get("session_id");
    
    if (success === "true" && sessionId) {
      verifyPurchase(sessionId);
    }
  }, []);

  const verifyPurchase = async (sessionId) => {
    try {
      const res = await fetch(`${API}/api/stripe/ai-credits/verify?session_id=${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success(`${data.credits} AI credits added to your account!`);
          fetchCredits();
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    } catch (error) {
      console.error("Failed to verify purchase:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={COMPANY_LOGO} alt="VEYRONIX" className="h-10 w-auto" />
            </Link>
            <Link to="/merchant/ai-social">
              <Button variant="ghost" className="text-gray-400 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to AI Studio
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        {/* Current Balance */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full mb-6">
            <Sparkles className="h-6 w-6 text-yellow-300" />
            <span className="text-lg font-medium">Your Balance:</span>
            {loadingCredits ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <span className="text-2xl font-bold">{currentCredits.toLocaleString()} credits</span>
            )}
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">
            Buy <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-transparent bg-clip-text">AI Credits</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Power your AI Studio with credits. Generate images, videos, scripts, and more.
          </p>
        </div>

        {/* Credit Usage Examples */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: Image, label: "AI Images", cost: "~10 credits" },
            { icon: Video, label: "Video Clips", cost: "~20 credits" },
            { icon: Mic, label: "Voice Overs", cost: "~5 credits" },
            { icon: FileText, label: "Scripts", cost: "~3 credits" }
          ].map((item, idx) => (
            <div key={idx} className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
              <item.icon className="h-8 w-8 text-indigo-400 mx-auto mb-2" />
              <p className="font-medium text-white">{item.label}</p>
              <p className="text-sm text-gray-500">{item.cost}</p>
            </div>
          ))}
        </div>

        {/* Credit Packages */}
        <div className="grid md:grid-cols-3 gap-8">
          {CREDIT_PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative rounded-2xl border p-8 transition-all duration-300 ${
                pkg.popular
                  ? "bg-gradient-to-b from-purple-900/30 to-slate-900/50 border-purple-500/50 scale-105"
                  : "bg-slate-900/50 border-gray-800 hover:border-indigo-500/50"
              }`}
              data-testid={`credit-package-${pkg.id}`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-purple-500 text-white text-sm font-medium rounded-full">
                    Best Value
                  </span>
                </div>
              )}

              <div className="flex items-center justify-center mb-6">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  pkg.popular ? "bg-purple-500/20" : "bg-indigo-500/20"
                }`}>
                  <Sparkles className={`h-8 w-8 ${pkg.popular ? "text-purple-400" : "text-indigo-400"}`} />
                </div>
              </div>

              <div className="text-center mb-6">
                <h3 className="font-heading text-3xl font-bold text-white mb-1">
                  {pkg.credits.toLocaleString()}
                </h3>
                <p className="text-gray-500">credits</p>
              </div>

              <div className="text-center mb-6">
                <span className="font-heading text-4xl font-bold">£{pkg.price}</span>
                <p className="text-sm text-gray-500 mt-1">
                  £{pkg.perCredit.toFixed(3)} per credit
                </p>
              </div>

              <Button
                onClick={() => handlePurchase(pkg)}
                disabled={loading === pkg.id}
                className={`w-full h-12 rounded-full font-medium mb-6 ${
                  pkg.popular
                    ? "bg-purple-500 hover:bg-purple-600 text-white"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white"
                }`}
                data-testid={`buy-credits-${pkg.id}`}
              >
                {loading === pkg.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Buy Now
                  </>
                )}
              </Button>

              <p className="text-sm text-gray-500 text-center mb-4">{pkg.description}</p>

              <div className="space-y-2">
                {pkg.uses.map((use, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-emerald-400" />
                    <span className="text-gray-400">{use}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Info */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>Credits never expire. All purchases are secured by Stripe.</p>
          <p className="mt-1">
            Questions? Contact <a href="mailto:support@veyronix.co.uk" className="text-indigo-400 hover:underline">support@veyronix.co.uk</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default AICreditsPage;
